import ShareIcon from '@mui/icons-material/ShareOutlined';
import Modal from '@mui/material/Modal';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { MAX_ATTEMPTS, TILE_COUNT } from '../constants';
import { useDailySeed } from '../hooks/useDailySeed';
import { Guess, useGuessHistory } from '../hooks/useGuessHistory';
import { useRoundEmojis } from '../hooks/useRoundEmojis';
import { useTodaysCountry } from '../providers/TodaysCountryProvider';
import { getPuzzleNumber } from '../utils/helpers';

interface PrankRevealModalProps {
  open: boolean;
  onClose: () => void;
  onPlayRealGame: () => void;
  prankSeed?: string;
}

const generateShareSquares = ({
  score,
  guesses,
}: {
  score: number | 'DNF';
  guesses: Guess[];
}) => {
  if (score === 'DNF') {
    return '🟥🟥🟥\n🟥🟥🟥\n';
  }

  const squares = Array(TILE_COUNT).fill('🟩');
  for (let i = 0; i < guesses.length - 1; i++) {
    squares[guesses[i].tile] = '🟥';
  }

  for (let i = 0; i < TILE_COUNT; i++) {
    if ((i + 1) % 3 === 0) {
      squares[i] += '\n';
    }
  }
  return squares.join('');
};

export function PrankRevealModal({
  open,
  onClose,
  onPlayRealGame,
  prankSeed,
}: PrankRevealModalProps) {
  const { t } = useTranslation();
  const { isArchive, dayString } = useTodaysCountry();
  const dailySeed = useDailySeed(isArchive);
  const effectiveSeed = prankSeed ?? dailySeed;
  const [guessHistory] = useGuessHistory(isArchive, prankSeed);
  const textEmojis = useRoundEmojis(isArchive);

  const shareText = useMemo(() => {
    const dailyGuesses = guessHistory[effectiveSeed] || [];
    const score = dailyGuesses.some(({ distance }) => distance === 0)
      ? dailyGuesses.length
      : 'DNF';

    const puzzleNumber = getPuzzleNumber(dayString);
    const squareString = generateShareSquares({
      score,
      guesses: dailyGuesses,
    });
    const date = dayjs(dayString).format('DD.MM.YYYY');

    return `#Flagle #${puzzleNumber} (${date}) ${
      score === 'DNF' ? 'X' : dailyGuesses.length
    }/${MAX_ATTEMPTS}\n${squareString}${textEmojis}Can you beat today's special game?\nhttps://www.flagle.io`;
  }, [guessHistory, effectiveSeed, dayString, textEmojis]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-slate-700 text-slate-100 shadow-2xl p-6 outline-none">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="rounded-xl p-3">
            <span className="text-7xl leading-none">🃏</span>
          </div>
          <h2 className="text-3xl font-extrabold uppercase tracking-wide text-yellow-300">
            April Fools!
          </h2>
          <p className="text-lg font-bold text-slate-100">
            World Government doesn&apos;t exist.
          </p>
          <p className="text-sm leading-relaxed text-slate-300">
            This flag belongs to the World Government from{' '}
            <em>One&nbsp;Piece</em>, a popular manga series.
          </p>
          <p className="text-sm leading-relaxed text-slate-300">
            Don&apos;t worry, your streak is safe &mdash; but you can play the{' '}
            <button
              type="button"
              className="inline underline decoration-dotted underline-offset-2 text-slate-100 hover:text-white transition-colors"
              onClick={onPlayRealGame}
            >
              real game
            </button>{' '}
            also!
          </p>

          <CopyToClipboard
            text={shareText}
            onCopy={() => toast.success(t('copy'), { autoClose: 2000 })}
            options={{ format: 'text/plain' }}
          >
            <button
              type="button"
              className="w-full py-3 px-4 rounded-full border-2 border-yellow-400 text-yellow-400 font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-yellow-400/10 transition-colors"
            >
              <ShareIcon fontSize="small" />
              Prank your friends
            </button>
          </CopyToClipboard>

          <div className="flex items-center gap-3 w-full text-slate-400 text-xs">
            <div className="flex-1 border-t border-slate-500" />
            <span>OR</span>
            <div className="flex-1 border-t border-slate-500" />
          </div>

          <button
            type="button"
            className="w-full py-3 px-4 rounded-full bg-slate-500 text-white font-bold uppercase tracking-wider hover:bg-slate-400 transition-colors"
            onClick={onPlayRealGame}
          >
            Play the real daily game
          </button>
        </div>
      </div>
    </Modal>
  );
}
